import { IAccessCenterContext } from "../../../Services/AccessCenterService";
import ReferenceModel from "../../../Models/ReferenceModel";
import PermissionCheck from "../../../Components/PermissionCheck";
import { DASHLY_LAYOUT_NARROW_MEDIA } from "../../../Util/layoutBreakpoints";

export const ACCESS_CENTER_USERS_TAB = "users";
export const ACCESS_CENTER_ROLES_VIEW = "roles";
export const ACCESS_CENTER_CRUD_FILTERS_PORTAL_ID = "access-center-crud-filters-portal";
/** @deprecated Legacy URL param; customer selection is stored in sessionStorage. */
export const ACCESS_CENTER_CUSTOMER_PARAM = "customer";
export const ACCESS_CENTER_SELECTED_CUSTOMER_KEY = "access-center.selected-customer";
export const ACCESS_CENTER_CRUD_DIALOG_CLASS = "access-center-dialog-form";
export const ACCESS_CENTER_CUSTOMER_TRAY_MEDIA = DASHLY_LAYOUT_NARROW_MEDIA;

export function scrollActiveRoleEditorTabIntoView(): void {
	window.requestAnimationFrame(() => {
		document
			.querySelector(".role-editor__tabs .p-tabview-nav li.p-highlight")
			?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
	});
}

export function getStoredAccessCenterCustomerUuid(): string | null {
	try {
		return sessionStorage.getItem(ACCESS_CENTER_SELECTED_CUSTOMER_KEY);
	} catch {
		return null;
	}
}

export function setStoredAccessCenterCustomerUuid(customerUuid: string | null): void {
	try {
		if (customerUuid) {
			sessionStorage.setItem(ACCESS_CENTER_SELECTED_CUSTOMER_KEY, customerUuid);
			return;
		}
		sessionStorage.removeItem(ACCESS_CENTER_SELECTED_CUSTOMER_KEY);
	} catch {
		// sessionStorage may be unavailable
	}
}

export function resolvePreferredCustomerUuid(legacyUrlCustomerUuid?: string | null): string | null {
	return getStoredAccessCenterCustomerUuid() ?? legacyUrlCustomerUuid ?? null;
}

export function buildAccessCenterPath(options?: {
	tab?: string | null;
}): string {
	const params = new URLSearchParams();
	if (options?.tab) {
		params.set("tab", options.tab);
	}
	const query = params.toString();
	return query ? `/management/access?${query}` : "/management/access";
}

export const ACCESS_CENTER_EDITOR_CREATE_ID = "new";

export function isAccessCenterCreateEditorId(id?: string | null): boolean {
	return id === ACCESS_CENTER_EDITOR_CREATE_ID;
}

export function buildUserEditorPath(userUuid: string): string {
	return `/management/access/user/${userUuid}`;
}

export function buildUserCreateEditorPath(customerUuid: string, customerName?: string): string {
	const params = new URLSearchParams({ customer: customerUuid });
	if (customerName) {
		params.set("customerName", customerName);
	}
	return `/management/access/user/${ACCESS_CENTER_EDITOR_CREATE_ID}?${params.toString()}`;
}

export function buildRoleCreateEditorPath(customerUuid: string, customerName?: string): string {
	const params = new URLSearchParams({ customer: customerUuid });
	if (customerName) {
		params.set("customerName", customerName);
	}
	return `/management/access/role/${ACCESS_CENTER_EDITOR_CREATE_ID}?${params.toString()}`;
}

/** CrudController POST returns the new entity uuid as `data` (string), not `{ uuid }`. */
export function extractCreatedEntityUuid(data: unknown): string | undefined {
	if (typeof data === "string" && data.length > 0) {
		return data;
	}
	if (data && typeof data === "object" && "uuid" in data) {
		const uuid = (data as { uuid?: unknown }).uuid;
		return typeof uuid === "string" && uuid.length > 0 ? uuid : undefined;
	}
	return undefined;
}

export function buildAccessCenterCrudInitial(customer: ReferenceModel) {
	return {
		customer,
		active: true,
	};
}

export function canShowCustomerList(context: IAccessCenterContext | null): boolean {
	return context?.showCustomerList === true || canAddCustomer(context);
}

export function resolveSelectedCustomer(
	context: IAccessCenterContext | null,
	selectedUuid?: string | null,
	selectedName?: string | null,
): string | null {
	if (!context) {
		return null;
	}
	if (selectedUuid && context.customers.some((customer) => customer.uuid === selectedUuid)) {
		return selectedUuid;
	}
	if (selectedName) {
		const matchedCustomer = context.customers.find((customer) => customer.name === selectedName);
		if (matchedCustomer?.uuid) {
			return matchedCustomer.uuid;
		}
	}
	if (context.defaultCustomerUuid) {
		return context.defaultCustomerUuid;
	}
	return context.customers[0]?.uuid ?? null;
}

export type AccessCenterViewMode = typeof ACCESS_CENTER_ROLES_VIEW | typeof ACCESS_CENTER_USERS_TAB;

export function resolveActiveViewMode(
	tabParam: string | null,
	canViewRoles: boolean,
	canViewUsers: boolean,
): AccessCenterViewMode {
	if (tabParam === ACCESS_CENTER_USERS_TAB && canViewUsers) {
		return ACCESS_CENTER_USERS_TAB;
	}
	if (canViewRoles) {
		return ACCESS_CENTER_ROLES_VIEW;
	}
	return ACCESS_CENTER_USERS_TAB;
}

export function canSwitchAccessCenterView(canViewRoles: boolean, canViewUsers: boolean): boolean {
	return canViewRoles && canViewUsers;
}

/** @deprecated Use resolveActiveViewMode instead. */
export function resolveActiveTabIndex(tabParam: string | null, canViewRoles: boolean, canViewUsers: boolean): number {
	if (tabParam === ACCESS_CENTER_USERS_TAB && canViewUsers) {
		return canViewRoles ? 1 : 0;
	}
	return 0;
}

export function findCustomerName(context: IAccessCenterContext | null, uuid: string | null): string {
	if (!context || !uuid) {
		return "";
	}
	return context.customers.find((customer) => customer.uuid === uuid)?.name ?? "";
}

export function canAccessAccessCenter(
	canViewCustomers: boolean,
	canViewRoles: boolean,
	canViewUsers: boolean,
): boolean {
	return canViewCustomers || canViewRoles || canViewUsers;
}

export function canAddCustomer(_context?: IAccessCenterContext | null): boolean {
	return PermissionCheck("Customer.Add") || PermissionCheck("SubCustomer.Add");
}

export function canEditCustomer(
	customerUuid?: string | null,
	primaryCustomerUuid?: string | null,
): boolean {
	if (!customerUuid) {
		return false;
	}
	const isPrimary = Boolean(primaryCustomerUuid && customerUuid === primaryCustomerUuid);
	if (isPrimary) {
		return PermissionCheck("Customer.Edit");
	}
	return PermissionCheck("Customer.Edit") || PermissionCheck("SubCustomer.Edit");
}

export function filterCustomersByName(customers: ReferenceModel[], filterText: string): ReferenceModel[] {
	const query = filterText.trim().toLowerCase();
	if (!query) {
		return customers;
	}
	return customers.filter((customer) => (customer.name ?? "").toLowerCase().includes(query));
}
